import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import type { AuditTrailSearchQuery } from '@skewb-works/contracts';
import { auditEntityTypeEnum, auditEventTypeEnum } from '@skewb-works/contracts';
import { X } from 'lucide-react';
import { formatEntityType, formatEventType } from '../utils/auditTrailFormatters';
import { UserCombobox } from './UserCombobox';

const ALL_SENTINEL = '__all__';
// Work orders are tracked in the audit trail but intentionally excluded from the
// entity type filter dropdown. Existing 'work_order' records still validate via the contract.
const ENTITY_TYPES = auditEntityTypeEnum.options.filter((type) => type !== 'work_order');
const EVENT_TYPES = auditEventTypeEnum.options;

interface AuditTrailFiltersProps {
  filters: Partial<AuditTrailSearchQuery>;
  onFilterChange: (filters: Partial<AuditTrailSearchQuery>) => void;
}

export function AuditTrailFilters({ filters, onFilterChange }: AuditTrailFiltersProps) {
  const hasActiveFilters =
    filters.entity_type ||
    filters.event_type ||
    filters.user_id ||
    filters.from_date ||
    filters.to_date;

  // SC-6601: a To Date earlier than the From Date is an invalid range. Flag it so the empty
  // result set isn't mistaken for "no activity"; the date inputs also constrain each other.
  const dateRangeInvalid = Boolean(
    filters.from_date && filters.to_date && filters.to_date < filters.from_date,
  );

  function updateFilter(updates: Partial<AuditTrailSearchQuery>) {
    onFilterChange({ ...filters, ...updates, skip: 0 });
  }

  // SC-6607: Clear removes ALL filters, including the From/To dates. Dates are optional on the
  // search API, so an empty range shows the full (paginated) log rather than an empty view.
  function clearFilters() {
    onFilterChange({
      limit: filters.limit ?? 50,
      skip: 0,
    });
  }

  return (
    <div className="flex flex-wrap items-end gap-3 px-6 pb-4">
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground">Entity Type</label>
        <Select
          value={filters.entity_type ?? ALL_SENTINEL}
          onValueChange={(val) => {
            if (val === ALL_SENTINEL) {
              updateFilter({ entity_type: undefined });
            } else {
              const parsed = auditEntityTypeEnum.safeParse(val);
              updateFilter({ entity_type: parsed.success ? parsed.data : undefined });
            }
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_SENTINEL}>All entities</SelectItem>
            {ENTITY_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {formatEntityType(type)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground">Event Type</label>
        <Select
          value={filters.event_type ?? ALL_SENTINEL}
          onValueChange={(val) => {
            if (val === ALL_SENTINEL) {
              updateFilter({ event_type: undefined });
            } else {
              const parsed = auditEventTypeEnum.safeParse(val);
              updateFilter({ event_type: parsed.success ? parsed.data : undefined });
            }
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_SENTINEL}>All events</SelectItem>
            {EVENT_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {formatEventType(type)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground">User</label>
        <UserCombobox
          value={filters.user_id}
          onSelect={(userId) => updateFilter({ user_id: userId })}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground">From Date</label>
        <Input
          type="date"
          className="w-[160px]"
          value={filters.from_date ?? ''}
          max={filters.to_date}
          onChange={(e) => updateFilter({ from_date: e.target.value || undefined })}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground">To Date</label>
        <Input
          type="date"
          className="w-[160px]"
          value={filters.to_date ?? ''}
          min={filters.from_date}
          aria-invalid={dateRangeInvalid}
          onChange={(e) => updateFilter({ to_date: e.target.value || undefined })}
        />
      </div>

      {hasActiveFilters && (
        <Button variant="ghost" onClick={clearFilters} className="gap-1">
          <X className="w-3.5 h-3.5" />
          Clear
        </Button>
      )}

      {dateRangeInvalid && (
        <p role="alert" className="basis-full text-xs text-red-500">
          &ldquo;To Date&rdquo; can&rsquo;t be earlier than &ldquo;From Date&rdquo;.
        </p>
      )}
    </div>
  );
}
